import React, { useEffect, useState } from 'react';

import { uiLogger } from '~/app';
import { LeagueType } from '~/server/src/dtos';
import {
  generateBotAvatarDataUrl,
  getInitials,
  getProfilePictureUrl,
  isLocalBot,
} from '~/utils/profilePicture';
import { cn } from '~/utils/styles';

interface ProfilePictureProps {
  username?: string;
  profilePicture?: string;
  toplistLeague?: LeagueType;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  userId?: string;
  isLocalMode?: boolean;
}

const ProfilePicture: React.FC<ProfilePictureProps> = ({
  username = '',
  profilePicture,
  toplistLeague = LeagueType.Bronze,
  size = 'md',
  className,
  userId,
  isLocalMode = false,
}) => {
  const [botAvatarUrl, setBotAvatarUrl] = useState<string | undefined>(undefined);
  const isLocalBotUser = isLocalBot(userId, isLocalMode);

  // Generate bot avatar only for local bots; when !isLocalBotUser we use getProfilePictureUrl in display
  useEffect(() => {
    if (isLocalBotUser && userId) {
      generateBotAvatarDataUrl(userId)
        .then(setBotAvatarUrl)
        .catch((error) => {
          uiLogger.warn({ err: error }, 'Failed to generate bot avatar');
          setBotAvatarUrl(undefined);
        });
    }
  }, [isLocalBotUser, userId]);

  // If local bot, use generated avatar (or initials until loaded); otherwise use profile picture URL
  const profilePictureUrl = isLocalBotUser
    ? (botAvatarUrl ?? undefined)
    : getProfilePictureUrl(profilePicture);
  const initials = getInitials(username);

  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-lg',
    xl: 'w-24 h-24 text-xl',
  };

  const leagueColors = {
    [LeagueType.Bronze]: 'bg-bronze',
    [LeagueType.Silver]: 'bg-silver',
    [LeagueType.Gold]: 'bg-gold',
  };

  return (
    <div
      className={cn(
        'flex items-center justify-center overflow-hidden rounded-full',
        sizeClasses[size],
        leagueColors[toplistLeague],
        className,
      )}
    >
      {profilePictureUrl ? (
        <img
          src={profilePictureUrl}
          alt={username}
          className="h-full w-full object-cover"
          onError={(e) => {
            // Fallback to initials if image fails to load
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML = `<span class="text-white font-semibold">${initials}</span>`;
            }
          }}
        />
      ) : (
        <span className="font-semibold text-white">{initials}</span>
      )}
    </div>
  );
};

export default ProfilePicture;
